import type {
  SerpRivalKeywordsStatus,
  TopSerpRivalsEstimate,
  TopSerpRivalsStatus
} from '../services/crawl';

/**
 * Copy and arithmetic for the Top SERP rivals chain (A3 / SS001-15, 16, 7).
 *
 * Pure on purpose. The two things this file decides — what the operator is told
 * they are about to spend, and what they are told it actually cost — are the
 * whole point of the confirm, so they get asserted in CI instead of being
 * reviewed by eye in a template.
 */

export function formatUsd(value: number): string {
  const safe = Number.isFinite(value) ? Math.max(0, value) : 0;
  // Sub-cent totals still cost money; "$0.00" would read as free.
  if (safe > 0 && safe < 0.01) return '<$0.01';
  return `$${safe.toFixed(2)}`;
}

/** Wall-clock duration, at the precision an operator actually cares about. */
export function formatDuration(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin > 0 ? `${hr}h ${remMin}m` : `${hr}h`;
}

/**
 * The visible ceiling line (SS001-15). Three genuinely different situations,
 * three different sentences — collapsing them into one safe phrase is how the
 * old MAX_RIVALS drop stayed invisible:
 *
 *   - capped: work is being deferred, and the operator must see how much
 *   - under the ceiling: a ceiling exists but is not biting
 *   - no ceiling: this environment turned the cap off; say so loudly
 */
export function rivalCeilingLabel(estimate: TopSerpRivalsEstimate | null): string | null {
  if (!estimate) return null;
  const { candidateFocals, trackedUrlsWithRival, ceiling, deferredFocals } = estimate;
  if (candidateFocals <= 0) return null;

  if (ceiling == null) {
    return `No ceiling configured — all ${fmt(candidateFocals)} page(s) with a rival ahead will run.`;
  }
  if (estimate.capped) {
    return (
      `Capped to the top ${fmt(trackedUrlsWithRival)} of ${fmt(candidateFocals)} page(s) with a rival ahead — `
      + `highest-opportunity first. The other ${fmt(deferredFocals)} stay available per page on demand, or in a later run.`
    );
  }
  return `${fmt(candidateFocals)} of ${fmt(candidateFocals)} page(s) with a rival ahead — under the ${fmt(ceiling)}-page ceiling, nothing deferred.`;
}

/** Can this chain be started at all, and if not, why? */
export function rivalChainBlockedReason(input: {
  estimate: TopSerpRivalsEstimate | null;
  rivalsStatus: TopSerpRivalsStatus | null;
  keywordsStatus: SerpRivalKeywordsStatus | null;
  starting: boolean;
}): string | null {
  if (input.starting) return 'Starting…';
  if (input.rivalsStatus?.status === 'building') return 'A rivals build is already running.';
  if (input.keywordsStatus?.status === 'building') return 'A rival keyword fetch is already running.';
  if (!input.estimate) return 'Cost preview has not loaded yet.';
  if (input.estimate.candidateFocals <= 0) {
    return 'No rivals to crawl — run URL SERP snapshots first, or you already rank top.';
  }
  return null;
}

/**
 * The confirm body. Everything the CEO call requires before the click: what
 * runs, unique pages, already-analyzed = $0, the estimated $ total, and the
 * ceiling that caps it.
 */
export function rivalChainConfirmMessage(estimate: TopSerpRivalsEstimate): string {
  const lines: string[] = [];
  lines.push('Run the Top SERP rivals chain now?');
  lines.push('');
  lines.push(
    `1. Crawl rival pages — ${fmt(estimate.crawlsNeeded)} of ${fmt(estimate.uniqueRivalCount)} unique rival page(s)`
    + `${estimate.alreadyAnalyzedCount > 0 ? `; ${fmt(estimate.alreadyAnalyzedCount)} already analyzed ($0)` : ''}`
    + ` × ${formatUsd(estimate.estimatedCostPerPage)}/page = ${formatUsd(estimate.estimatedCrawlCost)}`
  );
  lines.push(
    `2. Fetch rival ranked keywords (DataForSEO Labs) — up to ${fmt(estimate.keywordFetchesNeeded)} page(s)`
    + `${estimate.alreadyHaveKeywordsCount > 0 ? `; ${fmt(estimate.alreadyHaveKeywordsCount)} already have keywords ($0)` : ''}`
    + ` × ${formatUsd(estimate.estimatedCostPerKeywordUrl)}/page = ${formatUsd(estimate.estimatedKeywordsCost)}`
  );
  lines.push('');
  lines.push(`Estimated total: ${formatUsd(estimate.estimatedTotalCost)}`);

  const ceiling = rivalCeilingLabel(estimate);
  if (ceiling) {
    lines.push('');
    lines.push(ceiling);
  }

  lines.push('');
  lines.push('Step 2 starts on its own when step 1 finishes — this is the only confirm.');
  return lines.join('\n');
}

export interface RivalChainActuals {
  /** Wall time from the crawl starting to the last leg finishing. */
  durationMs: number | null;
  durationLabel: string | null;
  /** Billed cost across both legs. */
  costUsd: number;
  costLabel: string;
  /** False while any leg of the chain is still outstanding. */
  complete: boolean;
}

/**
 * SS001-7: the actual duration and cost of the run that just happened.
 *
 * Spans both legs when the chain ran, because one confirm bought both — a
 * readout covering only the crawl would understate what the operator paid.
 * Duration is measured start-of-crawl to end-of-keywords rather than summed,
 * so the queue wait between them is included; that gap is time they waited.
 */
export function rivalChainActuals(
  rivals: TopSerpRivalsStatus | null,
  keywords: SerpRivalKeywordsStatus | null
): RivalChainActuals {
  const incomplete: RivalChainActuals = {
    durationMs: null,
    durationLabel: null,
    costUsd: 0,
    costLabel: formatUsd(0),
    complete: false
  };
  if (!rivals || rivals.status !== 'ready' || !rivals.startedAt || !rivals.generatedAt) {
    return incomplete;
  }

  // Only fold the keyword leg in when it belongs to this run: a standalone
  // fetch from last week is not part of what this confirm bought.
  const chained =
    rivals.chainKeywords
    && !!keywords
    && keywords.chained
    && keywords.status === 'ready'
    && !!keywords.generatedAt;
  if (rivals.chainKeywords && !chained) return incomplete;

  const startMs = new Date(rivals.startedAt).getTime();
  const endMs = new Date(
    (chained ? keywords!.generatedAt : rivals.generatedAt) as string
  ).getTime();
  const durationMs =
    Number.isFinite(startMs) && Number.isFinite(endMs) && endMs >= startMs ? endMs - startMs : null;

  const costUsd =
    Math.round(((rivals.actualCostUsd || 0) + (chained ? keywords!.actualCostUsd || 0 : 0)) * 100)
    / 100;

  return {
    durationMs,
    durationLabel: formatDuration(durationMs),
    costUsd,
    costLabel: formatUsd(costUsd),
    complete: true
  };
}

function fmt(n: number): string {
  return (Number.isFinite(n) ? n : 0).toLocaleString('en-US');
}
